/**
 * /app/:slug — Page publique d'une app publiée (Phase 9).
 * PAS de Navbar site vitrine. Footer discret "Créé avec OpenMeta".
 * Fonctionne déconnecté. Utilise la langue de l'app.
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchPublicApp } from '@/lib/apps-api';
import { setPageMeta } from '@/lib/seo';
import { useTheme } from '@/lib/theme';
import { BlockPublicRenderer } from '@/components/builder/BlockCards';
import { Sparkles, ArrowLeft } from 'lucide-react';

export default function PublicApp() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, app: null, code: null });
  const { theme } = useTheme();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchPublicApp(slug);
      if (cancelled) return;
      if (res.ok) {
        setState({ loading: false, app: res.app, code: null });
        const isFr = (res.app.lang || 'fr') !== 'en';
        // La description est calculée côté serveur (description ou 1er paragraphe content)
        const description = res.app.description
          ? res.app.description
          : (isFr
              ? `${res.app.name} — une application créée avec OpenMeta.`
              : `${res.app.name} — an application built with OpenMeta.`);
        setPageMeta({
          title: `${res.app.name} — OpenMeta`,
          description: description.slice(0, 200),
          path: `/app/${slug}`,
          type: 'article',
        });
        document.documentElement.lang = res.app.lang || 'fr';
      } else {
        setState({ loading: false, app: null, code: res.code });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[var(--om-bg)]" data-testid="public-app-loading">
        <div className="om-container py-24 text-center text-sm text-[var(--om-muted-2)]">…</div>
      </div>
    );
  }

  if (!state.app) {
    // Fallback : détecter la langue depuis la préférence utilisateur (nav + localStorage)
    let lang = 'fr';
    try {
      const stored = window.localStorage.getItem('om_lang');
      if (stored === 'fr' || stored === 'en') lang = stored;
      else if ((window.navigator?.language || 'fr').toLowerCase().startsWith('en')) lang = 'en';
    } catch (_) {}
    const isFr = lang !== 'en';
    return (
      <div className="min-h-screen bg-[var(--om-bg)]" data-testid="public-app-notfound">
        <div className="om-container flex min-h-screen flex-col items-center justify-center py-16 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[var(--om-ink)]/5">
            <Sparkles size={22} strokeWidth={2.4} className="text-[var(--om-muted-2)]" />
          </div>
          <h1 className="mt-6 text-3xl font-extrabold text-[var(--om-ink)]">
            {isFr ? "Cette application n'est pas accessible." : 'This application is not accessible.'}
          </h1>
          <p className="mt-3 max-w-md text-sm text-[var(--om-muted)]">
            {isFr
              ? "Elle n'existe pas encore, a été dépubliée ou l'URL est incorrecte."
              : "It doesn't exist yet, has been unpublished, or the URL is incorrect."}
          </p>
          <Link to="/" className="om-btn-primary mt-8" data-testid="public-app-home-cta">
            <ArrowLeft size={14} strokeWidth={2.4} /> {isFr ? 'Retour vers OpenMeta' : 'Back to OpenMeta'}
          </Link>
        </div>
      </div>
    );
  }

  const app = state.app;
  const accent = app.accent || '#0A0E1A';
  const isFr = app.lang !== 'en';

  return (
    <div className="min-h-screen bg-[var(--om-bg)]" data-testid="page-public-app" data-slug={app.slug}>
      <header
        className="border-b border-[var(--om-border)] bg-[var(--om-bg-pure)]"
      >
        <div className="om-container flex h-16 items-center justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-white" style={{ background: accent }} aria-hidden>
              <Sparkles size={14} strokeWidth={2.4} />
            </span>
            <div className="min-w-0">
              <div className="truncate text-sm font-extrabold text-[var(--om-ink)]" data-testid="public-app-name">{app.name}</div>
              {app.description && <div className="hidden truncate text-[11px] text-[var(--om-muted)] md:block">{app.description}</div>}
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--om-muted-2)]">
            {isFr ? 'App OpenMeta' : 'OpenMeta App'}
          </div>
        </div>
      </header>

      <main className="py-8 md:py-12">
        <div className="om-container max-w-3xl space-y-6">
          {(app.blocks || []).map((b) => (
            <BlockPublicRenderer key={b.id} block={b} slug={app.slug} accent={accent} lang={app.lang} />
          ))}
        </div>
      </main>

      <footer className="border-t border-[var(--om-border)] bg-[var(--om-bg-pure)] py-6">
        <div className="om-container flex items-center justify-between text-[11px] text-[var(--om-muted)]">
          <span>© {new Date().getFullYear()} {app.name}</span>
          <a
            href="/"
            className="inline-flex items-center gap-1 font-semibold text-[var(--om-ink)] hover:underline"
            data-testid="public-app-openmeta-link"
          >
            {isFr ? 'Créé avec OpenMeta' : 'Built with OpenMeta'} →
          </a>
        </div>
      </footer>
    </div>
  );
}
